import { canUseDOM } from '@ps/web-core-utils';
import type { AttributeBlock, CasinoGameDeck } from '@ps/web-martech-cms-types/CasinoGameDeck';
import { get, isEmpty } from 'lodash';
import { takeUntil } from 'rxjs/operators';

import { preload, userUpdates } from '@swf/casino';
import type { Card, Hand } from '@swf/casino';
import { CasinoJoinOperand } from '@swf/core/enums';
import { areGamesVisibleByConfig } from '@swf/core/models/casino/utils';
import type { WidgetEffectParams } from '@swf/core/store';
import type { CasinoGame, StreamData } from '@swf/core/types/CasinoGame';
import { currencyChange } from '@swf/core/utils/actions';

import type { WidgetDispatch, WidgetSelect } from './types';

export const updater = (update: StreamData<CasinoGame>, hand: AttributeBlock) => {
  const cards =
    hand.cards
      ?.map((card): Card => {
        const maxGames = card.max_games || get(card, 'maxGames') || 9;

        return {
          baseColor: hand.base_color,
          title: card.title,
          subTitle: card.sub_title || get(card, 'subTitle'),
          initialGames: update.games
            ?.filter((game) => card.tag && game.tags?.includes(card.tag))
            .slice(0, maxGames),
          games: update.games
            ?.filter((game) => card.tag && game.tags?.includes(card.tag))
            .slice(0, maxGames),
          currentPage: 0,
          maxGames,
          hasNextButton: card.has_next_button || get(card, 'hasNextButton')
        };
      })
      .filter((card: Card) => card.games.length) || [];

  return {
    title: { defaultMessage: hand.title, id: hand.title, track: hand.title_tracking },
    baseColor: hand.base_color,
    cards
  };
};

const updateCards = (cardIndex: number, hand: Hand, nextGames: CasinoGame[]) => {
  const card = hand.cards[cardIndex];
  const updatedCard = {
    ...card,
    currentPage: nextGames?.length ? card.currentPage + 1 : 0,
    games: nextGames?.length ? nextGames : card.initialGames || []
  };

  return {
    ...hand,
    cards: hand.cards.map((card, i) => (i === cardIndex ? updatedCard : card))
  };
};

export const effects = {
  preload,
  userUpdates,
  async fetch(_payload: undefined, params: WidgetEffectParams<CasinoGameDeck>) {
    const { actions, dispatch, request, requestStream, rootState, select, widget } = params;
    const { params: body, game_service: service, uid, unmount } = widget;
    const widgetDispatch: WidgetDispatch = dispatch[uid];
    const widgetSelect: WidgetSelect = select[uid];
    const data = widgetSelect.get(rootState);

    widgetDispatch.setLoading(true);
    if (
      !areGamesVisibleByConfig({
        hasState: isEmpty(data),
        params,
        service
      })
    ) {
      return;
    }

    const promiseResults =
      widget.attributes?.map((hand) => {
        const str = (hand.cards?.map((card) => card.tag) || []).filter((tag) => !!tag) as string[];

        const subQuery = {
          service,
          body: {
            ...body,
            includeTags: [...body.include_tags, ...str],
            ims: { tags: body.ims.icon_tags },
            qry: [
              {
                formalparamcode: '_group',
                values: hand.cards
                  ?.filter((card) => !!card.tag)
                  .map((card) => ({
                    formalparamcode: 'tagname',
                    join: 1,
                    operand: 1,
                    values: [hand.tag, card.tag]
                  })),
                join: CasinoJoinOperand.OR,
                dynamic: false
              }
            ],
            psz: (hand.cards?.length || 1) * 9
          }
        };

        return new Promise<Hand>((resolve, reject) => {
          if (!canUseDOM) {
            try {
              request<StreamData<CasinoGame>>(subQuery).then(({ body: data }) =>
                updater(data, hand)
              );
            } catch (e) {
              reject({ cards: [] });
            }
          } else {
            requestStream<StreamData<CasinoGame>>(subQuery, undefined)
              .pipe(takeUntil(currencyChange(actions) as typeof actions))
              .subscribe((update) => resolve(updater(update, hand)));
          }
        });
      }) || [];

    Promise.all(promiseResults).then((hands) =>
      widgetDispatch.set({
        hands: hands.filter((hand) => hand.cards.length > 0),
        isLoading: false,
        preloaded: false
      })
    );

    unmount.subscribe(widgetDispatch.clear);
  },
  async loadNextPage(
    _payload: { handIndex: number; cardIndex: number },
    params: WidgetEffectParams<CasinoGameDeck>
  ) {
    const { dispatch, widget, select, rootState, requestStream } = params;
    const { uid } = widget;
    const widgetDispatch: WidgetDispatch = dispatch[uid];
    const widgetSelect: WidgetSelect = select[uid];
    const { hands, preloaded } = widgetSelect.get(rootState);

    widgetDispatch.setLoading(true);

    const { params: body, game_service: service } = widget;
    const handTag = widget.attributes[_payload.handIndex]?.tag;
    const card = widget.attributes[_payload.handIndex]?.cards[_payload.cardIndex];
    const { currentPage, maxGames } =
      widgetSelect.get(rootState).hands[_payload.handIndex].cards[_payload.cardIndex];

    const query = {
      service,
      body: {
        ...body,
        includeTags: [...body.include_tags, card?.tag],
        ims: { tags: body.ims.icon_tags },
        qry: [
          {
            formalparamcode: '_group',
            values: [
              {
                formalparamcode: 'tagname',
                join: 1,
                operand: 1,
                values: [handTag, card?.tag]
              }
            ],
            join: CasinoJoinOperand.OR,
            dynamic: false
          }
        ],
        pg: currentPage + 1,
        psz: Math.min(maxGames, 9)
      }
    };

    const obs = requestStream<StreamData<CasinoGame>>(query, undefined).subscribe((update) => {
      const updatedHands = hands.toSpliced(
        _payload.handIndex,
        1,
        updateCards(_payload.cardIndex, hands[_payload.handIndex], update.games)
      );

      widgetDispatch.set({
        hands: updatedHands,
        isLoading: false,
        preloaded
      });

      obs.unsubscribe();
    });
  }
};
